import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSubscription } from '@apollo/client';
import { PageTitle } from '@oysterjs/ui/page';
import { Button } from '@oysterjs/ui/button';
import { Input } from '@oysterjs/ui/input';
import { client } from '~/api/client';
import {
  useCreateWorkflowExecutionMutation,
  CreateWorkflowExecutionMutationVariables,
  WatchWorkflowExecutionDocument,
  WatchWorkflowExecutionSubscription,
  WatchWorkflowExecutionSubscriptionVariables,
  WorkflowExecutionState,
  WorkflowTaskState
} from '~/api/graphql';
import {
  flattenEventsFromPayload,
  eventTypeColor,
  topoSortTasks,
  formatDefinitionKey,
  formatDuration,
  taskStateIndicator,
  collectArtifacts,
  WorkflowArtifacts
} from './utils';

export const WorkflowEventStream = (props: { executionId: string; executionVersion: number }) => {
  const logEndRef = React.useRef<HTMLDivElement>(null);

  const { data, error } = useSubscription<
    WatchWorkflowExecutionSubscription,
    WatchWorkflowExecutionSubscriptionVariables
  >(WatchWorkflowExecutionDocument, {
    variables: { id: props.executionId, version: props.executionVersion },
    client
  });

  const execution = data?.watchWorkflowExecution ?? null;

  const tasks = React.useMemo(() => topoSortTasks(execution?.tasks ?? []), [data]);

  const events = React.useMemo(
    () => (execution ? flattenEventsFromPayload(execution) : []),
    [data]
  );

  const artifacts = React.useMemo(() => (execution ? collectArtifacts(execution) : []), [data]);

  React.useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [events.length]);

  return (
    <>
      <p className="text-xs text-neutral-500 dark:text-neutral-400 font-mono">
        Execution: {props.executionId} @ v{props.executionVersion}
        {execution?.state && <> — {execution.state}</>}
        {execution?.state &&
          (execution.state === WorkflowExecutionState.Completed ||
            execution.state === WorkflowExecutionState.Failed) &&
          (() => {
            const d = formatDuration(execution.startedAt, execution.completedAt);
            return d ? <> ({d})</> : null;
          })()}
        {execution?.state === WorkflowExecutionState.Failed && execution.error && (
          <span className="text-red-600 dark:text-red-400"> — {execution.error.message}</span>
        )}
      </p>

      {error && (
        <p className="text-xs text-red-600 dark:text-red-400 font-mono">
          Subscription error: {error.message}
        </p>
      )}

      {tasks.length > 0 && (
        <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900/50 overflow-hidden">
          <div className="p-2 border-b border-neutral-200 dark:border-neutral-700 bg-neutral-100/80 dark:bg-neutral-800/50 text-xs text-neutral-500 dark:text-neutral-400">
            Task status
          </div>
          <div className="p-2 space-y-1">
            {tasks.map((task) => {
              const indicator = taskStateIndicator(task.state);
              const duration = formatDuration(task.startedAt, task.completedAt);
              return (
                <div key={task.id} className="flex items-start gap-2 text-xs font-mono">
                  <span className="flex items-center gap-1.5 shrink-0 pt-px">
                    <span
                      className={`inline-block h-2 w-2 rounded-full ${indicator.bg} ${task.state === WorkflowTaskState.Running ? 'animate-pulse' : ''}`}
                    />
                    <span className={`w-16 ${indicator.color}`}>{indicator.label}</span>
                  </span>
                  <span className="text-neutral-600 dark:text-neutral-300">
                    {formatDefinitionKey(task.definitionKey)}
                  </span>
                  <span className="text-neutral-400 dark:text-neutral-500">
                    [{task.id.slice(0, 12)}]
                  </span>
                  {duration && (
                    <span className="text-neutral-400 dark:text-neutral-500">{duration}</span>
                  )}
                  {task.error && (
                    <span className="text-red-600 dark:text-red-400">— {task.error.message}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div
        className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900/50 overflow-hidden"
        style={{ minHeight: 320 }}
      >
        <div className="p-2 border-b border-neutral-200 dark:border-neutral-700 bg-neutral-100/80 dark:bg-neutral-800/50 text-xs text-neutral-500 dark:text-neutral-400">
          Unified task event stream
        </div>
        <div className="p-2 overflow-auto font-mono text-xs max-h-[60vh] min-h-[280px]">
          {events.length === 0 && (
            <div className="text-neutral-400 dark:text-neutral-500 py-4">Waiting for events…</div>
          )}
          {events.map((e) => (
            <div
              key={e.eventId}
              className="py-0.5 border-b border-neutral-100 dark:border-neutral-800/50 last:border-0 flex gap-2 flex-wrap items-baseline"
            >
              <span className="shrink-0 text-neutral-400 dark:text-neutral-500 tabular-nums">
                {new Date(e.createdAt).toISOString().split('T')[1]}
              </span>
              <span className="shrink-0 font-semibold text-neutral-600 dark:text-neutral-400">
                [{e.taskId.slice(0, 12)}]
              </span>
              <span className="shrink-0 text-neutral-500 dark:text-neutral-400">
                {formatDefinitionKey(e.definitionKey)}
              </span>
              <span className={`shrink-0 ${eventTypeColor(e.type)}`}>{e.type}</span>
              <span className="min-w-0 break-words text-neutral-700 dark:text-neutral-300">
                {e.title}{' '}
                {!!e.progress &&
                  ` (${new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 1 }).format(e.progress)})`}
                {e.content != null && (
                  <span className="text-neutral-500 dark:text-neutral-400">
                    {' '}
                    — {e.content.slice(0, 120)}
                    {e.content.length > 120 ? '…' : ''}
                  </span>
                )}
              </span>
            </div>
          ))}
          <div ref={logEndRef} />
        </div>
      </div>

      <WorkflowArtifacts artifacts={artifacts} />
    </>
  );
};

export const WorkflowsDebugPage = () => {
  const { id: paramId, version: paramVersion } = useParams<{ id?: string; version?: string }>();
  const navigate = useNavigate();

  const executionId = paramId ?? null;
  const executionVersion = paramVersion != null ? parseInt(paramVersion, 10) : 1;

  const [definitionId, setDefinitionId] = React.useState('default');
  const [definitionVersion, setDefinitionVersion] = React.useState(1);

  const [createWorkflow, { loading: creating }] = useCreateWorkflowExecutionMutation({
    client
  });

  const handleStart = async () => {
    try {
      const res = await createWorkflow({
        variables: {
          input: {
            definitionId,
            definitionVersion,
            inputs: []
          }
        } as CreateWorkflowExecutionMutationVariables
      });
      const exec = res.data?.createWorkflowExecution;
      if (exec) {
        navigate(`/app/workflows/${exec.id}/${exec.version}`);
      }
    } catch {
      // Error surfaced by UI / Apollo
    }
  };

  const handleClear = () => {
    navigate('/app/workflows');
  };

  return (
    <PageTitle
      title="Workflow debug"
      description="Trigger a workflow and stream task events into a unified log. Temporary debugging tool."
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
              Definition ID
            </span>
            <Input
              type="text"
              value={definitionId}
              onChange={(e) => setDefinitionId(e.target.value)}
              className="w-40 font-mono text-sm"
              placeholder="default"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
              Version
            </span>
            <Input
              type="number"
              min={1}
              value={definitionVersion}
              onChange={(e) => setDefinitionVersion(parseInt(e.target.value, 10) || 1)}
              className="w-20 font-mono text-sm"
            />
          </label>
          <Button onClick={handleStart} disabled={creating} color="dark" className="text-sm">
            {creating ? 'Starting…' : 'Start workflow'}
          </Button>
          {executionId && (
            <Button onClick={handleClear} color="neutral" className="text-sm">
              Clear
            </Button>
          )}
        </div>

        {executionId && executionVersion != null && (
          <WorkflowEventStream executionId={executionId} executionVersion={executionVersion} />
        )}
      </div>
    </PageTitle>
  );
};
